import React from 'react';
import { ThemeProvider } from 'styled-components';
import theme from '@/theme';
import { EnmonLoader } from '@/components/Loaders/EnmonLoader/EnmonLoader';
import { createRoot, type Root } from 'react-dom/client';

class GlobalLoaderManager {
  private container: HTMLDivElement | null = null;

  private isVisible = false;

  private startTime: number | null = null;

  private hideTimeout: NodeJS.Timeout | null = null;

  private minimumDisplayTime = 1500;

  private root: Root | null = null;

  constructor() {
    this.init();
    this.setupCleanupListeners();
  }

  private setupCleanupListeners() {
    if (typeof window === 'undefined') return; // SSR protection

    window.addEventListener('beforeunload', () => this.cleanup());
    window.addEventListener('unload', () => this.cleanup());
  }

  private init() {
    if (typeof window === 'undefined') return; // SSR protection

    this.container = document.createElement('div');
    this.container.id = 'global-loader-container';
    this.container.style.position = 'fixed';
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.width = '100vw';
    this.container.style.height = '100vh';
    this.container.style.zIndex = '10000';
    this.container.style.pointerEvents = 'none';

    document.body.appendChild(this.container);

    this.root = createRoot(this.container);
  }

  private render() {
    if (!(this.container && this.root)) return;

    if (this.isVisible) {
      this.root.render(
        React.createElement(
          ThemeProvider,
          { theme },
          React.createElement(EnmonLoader, {
            size: 'large',
          }),
        ),
      );
    } else {
      this.root.render(null);
    }
  }

  show() {
    if (this.isVisible) return;

    this.isVisible = true;
    if (!this.startTime) {
      this.startTime = Date.now();
    }

    if (this.container) {
      this.container.style.pointerEvents = 'auto';
    }

    this.render();
  }

  hide() {
    if (!(this.isVisible && this.startTime)) return;

    const elapsedTime = Date.now() - this.startTime;
    const remainingTime = this.minimumDisplayTime - elapsedTime;

    const doHide = () => {
      this.isVisible = false;
      this.startTime = null;

      if (this.container) {
        this.container.style.pointerEvents = 'none';
      }

      this.render();
    };

    if (remainingTime > 0) {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
      }

      this.hideTimeout = setTimeout(doHide, remainingTime);
    } else {
      doHide();
    }
  }

  isActive() {
    return this.isVisible;
  }

  cleanup() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    if (this.root) {
      this.root.unmount();
    }

    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

export const globalLoader = new GlobalLoaderManager();
